import { QrCodeScanner } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';
import { Scanner, IDetectedBarcode } from '@yudiel/react-qr-scanner';


const FONT_FAMILY =
  'Poppins, Inter, system-ui, -apple-system, Segoe UI, Roboto, sans-serif';

const SECTION_SX = {
  bgcolor: '#FFFFFF',
  borderRadius: '24px',
  p: 3,
  mb: 3,
  boxShadow: '0 8px 24px rgba(0,0,0,0.06)',
};

const SECTION_HEADING_SX = {
  fontFamily: FONT_FAMILY,
  fontWeight: 700,
  fontSize: 18,
  color: '#000000',
  mb: 1,
};

interface ProviderSectionProps {
  onJoinManually: (code: string) => void;
}

const ProviderSection: React.FC<ProviderSectionProps> = ({ onJoinManually }) => {
  const [manualDialogOpen, setManualDialogOpen] = useState(false);
  const [scannerDialogOpen, setScannerDialogOpen] = useState(false);
  const [inviteCode, setInviteCode] = useState('');
  const [scannerError, setScannerError] = useState<string | null>(null);

  const handleSubmit = (): void => {
    const normalizedCode = inviteCode.trim().replace(/[\s-]/g, '').toUpperCase();
    if (!normalizedCode) {
      return;
    }
    setManualDialogOpen(false);
    setInviteCode('');
    onJoinManually(normalizedCode);
  };

  const extractCodeFromScan = (value: string): string => {
    const trimmedValue = value.trim();
    const joinMatch = trimmedValue.match(/\/join\/([^/?#]+)/i);
    const rawCode = joinMatch ? joinMatch[1] : trimmedValue;
    return rawCode.replace(/[\s-]/g, '').toUpperCase();
  };

  const handleScan = (detectedCodes: IDetectedBarcode[]) => {
    if (detectedCodes && detectedCodes.length > 0) {
      const code = extractCodeFromScan(detectedCodes[0].rawValue);
      setScannerDialogOpen(false);
      onJoinManually(code);
    }
  };

  const handleError = (error: unknown) => {
    console.error('QR Scanner error:', error);
    setScannerError('Unable to access camera or scan QR code. Please enter the code manually.');
  };

  return (
    <Box sx={SECTION_SX}>
      <Typography sx={SECTION_HEADING_SX}>Provider</Typography>
      <Typography sx={{ fontFamily: FONT_FAMILY, color: '#4A5568', mb: 2 }}>
        Use an option below to join a Provider (only by invitation).
      </Typography>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
        <Button
          variant="contained"
          startIcon={<QrCodeScanner />}
          onClick={() => setScannerDialogOpen(true)}
          sx={{
            bgcolor: '#9ED8FF',
            color: '#000000',
            fontFamily: FONT_FAMILY,
            fontWeight: 700,
            borderRadius: '12px',
            px: 3,
            py: 1,
            textTransform: 'none',
            '&:hover': { bgcolor: '#7BCBFF' },
          }}
        >
          Scan QR Code
        </Button>
        <Button
          variant="contained"
          onClick={() => setManualDialogOpen(true)}
          sx={{
            bgcolor: '#9ED8FF',
            color: '#000000',
            fontFamily: FONT_FAMILY,
            fontWeight: 700,
            borderRadius: '12px',
            px: 3,
            py: 1,
            textTransform: 'none',
            '&:hover': { bgcolor: '#7BCBFF' },
          }}
        >
          Enter Code Manually
        </Button>
      </Box>

      <Dialog
        open={manualDialogOpen}
        onClose={() => setManualDialogOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle sx={{ fontFamily: FONT_FAMILY, fontWeight: 700 }}>
          Enter Provider invite code
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            autoFocus
            label="Invite code"
            value={inviteCode}
            onChange={(event) => setInviteCode(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                handleSubmit();
              }
            }}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setManualDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={!inviteCode.trim()}
          >
            Continue
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={scannerDialogOpen}
        onClose={() => setScannerDialogOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle sx={{ fontFamily: FONT_FAMILY, fontWeight: 700 }}>
          Scan Provider QR code
        </DialogTitle>
        <DialogContent>
          {scannerError && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {scannerError}
            </Alert>
          )}
          {!scannerError && (
            <Box sx={{ width: '100%', borderRadius: '16px', overflow: 'hidden' }}>
              <Scanner
                onScan={handleScan}
                onError={handleError}
                formats={['qr_code']}
                components={{ finder: false }}
              />
              <Typography
                sx={{ fontFamily: FONT_FAMILY, color: '#4A5568', mt: 2 }}
              >
                Point your camera at the Provider invite QR code.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setScannerDialogOpen(false)}>Cancel</Button>
          {scannerError && (
            <Button
              variant="contained"
              onClick={() => {
                setScannerDialogOpen(false);
                setManualDialogOpen(true);
              }}
            >
              Enter Code Manually
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProviderSection;
